import { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn, formatCurrency } from "@/lib/utils";
import type { Position } from "@/solana/programs/types";
import { PositionsApi } from "@/backend/api-client";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { useAccountStore, usePositionTpSl } from "@/backend/store";
import { toast } from "sonner";
import { X, Minus, Plus, ArrowLeftRight, Loader2 } from "lucide-react";
import { useMarketPrice } from "@/backend/store";
import { PositionManager } from "@/solana/programs/position-manager";
import { useMarketPrecision } from "@/hooks/use-price-precision";
import type { TpSlTarget, TpSlTargetDto } from "@nordex/core";

// Maximum characters allowed in price inputs (prevents huge numbers)
const MAX_PRICE_LENGTH = 12;

// Slider configurations
const TP_SLIDER_MAX = 150; // 150% max ROI for take profit
const SL_SLIDER_MAX = 75;  // 75% max loss for stop loss

type EditTpSlModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  position: Position | null;
  onSuccess?: () => void;
};

// Track which input is actively being edited
type ActiveInput = "tpPrice" | "tpRoi" | "slPrice" | "slRoi" | null;

// Tab mode for TP/SL
type TabMode = "standard" | "partial";

// Partial target input type for UI
type PartialTargetInput = {
  id: string;
  targetType: "take_profit" | "stop_loss";
  triggerPrice: string;
  amountType: "percentage" | "absolute";
  amountValue: string;
  roi: string; // calculated
};

// Price input with +/- buttons
const PriceInput = ({
  value,
  onChange,
  onClear,
  onFocus,
  onBlur,
  onIncrement,
  onDecrement,
  placeholder,
  suffix,
}: {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onIncrement: () => void;
  onDecrement: () => void;
  placeholder: string;
  suffix: string;
}) => (
  <div
    className={cn(
      "flex items-center rounded-lg border bg-muted/30 transition-all duration-200 overflow-hidden",
      "border-border hover:border-muted-foreground/60",
      "focus-within:border-foreground/70 dark:focus-within:border-white/80"
    )}
  >
    <input
      type="text"
      inputMode="decimal"
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        const cleaned = e.target.value.replace(/[^0-9.]/g, "");
        if (cleaned.length <= MAX_PRICE_LENGTH) onChange(cleaned);
      }}
      onFocus={onFocus}
      onBlur={onBlur}
      className={cn(
        "flex-1 min-w-0 bg-transparent px-3 py-2.5 text-sm outline-none placeholder:text-muted-foreground/50",
        "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      )}
    />
    {value && (
      <button
        type="button"
        onClick={onClear}
        className="px-1.5 text-muted-foreground/50 hover:text-foreground transition-colors flex-shrink-0"
      >
        <X className="size-3.5" />
      </button>
    )}
    <span className="pr-2 text-sm text-muted-foreground/60 flex-shrink-0">{suffix}</span>
    <div className="flex items-center border-l border-border">
      <button
        type="button"
        onClick={onDecrement}
        className="px-2.5 py-2.5 text-muted-foreground hover:text-foreground transition-colors"
      >
        <Minus className="size-3.5" />
      </button>
      <div className="w-px h-4 bg-border" />
      <button
        type="button"
        onClick={onIncrement}
        className="px-2.5 py-2.5 text-muted-foreground hover:text-foreground transition-colors"
      >
        <Plus className="size-3.5" />
      </button>
    </div>
  </div>
);

// ROI percentage input
const RoiInput = ({
  value,
  onChange,
  onClear,
  onFocus,
  onBlur,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder: string;
}) => (
  <div
    className={cn(
      "flex items-center rounded-lg border bg-muted/30 transition-all duration-200 overflow-hidden",
      "border-border hover:border-muted-foreground/60",
      "focus-within:border-foreground/70 dark:focus-within:border-white/80"
    )}
  >
    <input
      type="text"
      inputMode="decimal"
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        const cleaned = e.target.value.replace(/[^0-9.-]/g, "");
        if (cleaned.length <= 8) onChange(cleaned);
      }}
      onFocus={onFocus}
      onBlur={onBlur}
      className={cn(
        "flex-1 min-w-0 bg-transparent px-3 py-2.5 text-sm outline-none placeholder:text-muted-foreground/50 text-right",
        "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      )}
    />
    {value && (
      <button
        type="button"
        onClick={onClear}
        className="px-1.5 text-muted-foreground/50 hover:text-foreground transition-colors flex-shrink-0"
      >
        <X className="size-3.5" />
      </button>
    )}
    <span className="pr-3 text-sm text-muted-foreground/60 flex-shrink-0">%</span>
  </div>
);

// ROI Slider component - clean design like other sliders
const RoiSlider = ({
  value,
  onChange,
  max,
}: {
  value: number;
  onChange: (value: number) => void;
  max: number;
  color?: "green" | "red"; // kept for backwards compatibility but not used
}) => {
  // Calculate progress percentage
  const progressPercent = Math.min((value / max) * 100, 100);
  
  // Determine if this is TP slider (max=150) or SL slider (max=75)
  const isTPSlider = max > 100;
  
  // Calculate marker positions
  const markers = isTPSlider 
    ? [
        { value: 50, position: (50 / max) * 100 },
        { value: 100, position: (100 / max) * 100 },
      ]
    : [
        { value: 25, position: (25 / max) * 100 },
        { value: 50, position: (50 / max) * 100 },
      ];
  
  return (
    <div className="space-y-1.5">
      <div className="relative h-5 flex items-center">
        {/* Track background */}
        <div className="absolute left-0 right-0 h-1 rounded-full bg-border/40" />
        
        {/* Progress fill */}
        <div 
          className="absolute left-0 h-1 rounded-full bg-muted-foreground/30 pointer-events-none"
          style={{ width: `${progressPercent}%` }}
        />
        
        {/* Percentage markers */}
        {markers.map((marker) => (
          <div 
            key={marker.value}
            className="absolute w-0.5 h-2 bg-muted-foreground/40 rounded-full z-[1]"
            style={{ left: `${marker.position}%`, transform: 'translateX(-50%)' }}
          />
        ))}
        
        {/* Native range input */}
        <input
          type="range"
          min="0"
          max={max}
          step="1"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="absolute inset-0 w-full cursor-pointer z-10 roi-slider"
          style={{
            WebkitAppearance: 'none',
            background: 'transparent',
          }}
        />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground/50 relative">
        <span>0</span>
        {markers.map((marker) => (
          <span 
            key={marker.value}
            className="absolute"
            style={{ left: `${marker.position}%`, transform: 'translateX(-50%)' }}
          >
            {marker.value}%
          </span>
        ))}
        <span>{max}%</span>
      </div>
      <style>{`
        .roi-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: white;
          border: 2px solid rgba(255,255,255,0.8);
          cursor: grab;
          box-shadow: 0 1px 4px rgba(0,0,0,0.4);
          margin-top: -5px;
        }
        .roi-slider::-webkit-slider-thumb:active {
          cursor: grabbing;
          transform: scale(1.15);
        }
        .roi-slider::-webkit-slider-runnable-track {
          height: 4px;
          background: transparent;
          border-radius: 2px;
        }
        .roi-slider::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: white;
          border: 2px solid rgba(255,255,255,0.8);
          cursor: grab;
          box-shadow: 0 1px 4px rgba(0,0,0,0.4);
        }
        .roi-slider::-moz-range-thumb:active {
          cursor: grabbing;
          transform: scale(1.15);
        }
        .roi-slider::-moz-range-track {
          height: 4px;
          background: transparent;
          border-radius: 2px;
        }
      `}</style>
    </div>
  );
};

export const EditTpSlModal = ({
  open,
  onOpenChange,
  position,
  onSuccess,
}: EditTpSlModalProps) => {
  const { userId } = useUnifiedAuth();
  const [tabMode, setTabMode] = useState<TabMode>("standard");
  const [tpPrice, setTpPrice] = useState("");
  const [slPrice, setSlPrice] = useState("");
  const [tpRoi, setTpRoi] = useState("");
  const [slRoi, setSlRoi] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Partial mode state
  const [partialTargets, setPartialTargets] = useState<PartialTargetInput[]>([]);
  const [isLoadingPartialTargets, setIsLoadingPartialTargets] = useState(false);
  
  // Track which input is being actively edited to prevent overwriting
  const activeInputRef = useRef<ActiveInput>(null);
  
  // Track if modal has been initialized to prevent re-initialization on store updates
  const hasInitializedRef = useRef(false);

  const effectiveUserId = userId;

  // Get real-time market price from Zustand store (WebSocket updates)
  const currentPrice = useMarketPrice(position?.market ?? "BTC-USD");
  
  // Get market precision for dynamic formatting
  const { quotePrecision } = useMarketPrecision(position?.market ?? "BTC-USD");

  // Get current TP/SL from store (always fresh data, not stale position prop)
  const { takeProfit: storeTakeProfit, stopLoss: storeStopLoss } = usePositionTpSl(position?.market ?? "");

  // Calculate current unrealized PnL
  const currentPnl =
    position && currentPrice > 0
      ? PositionManager.calculateUnrealizedPnl(position, currentPrice)
      : 0;
  const currentPnlPercent =
    position && currentPrice > 0
      ? PositionManager.calculatePnlPercentage(position, currentPrice)
      : 0;

  // Initialize form values when dialog opens (only once per open)
  // This prevents race conditions when store updates during save
  useEffect(() => {
    // Reset initialization flag when modal closes
    if (!open) {
      hasInitializedRef.current = false;
      return;
    }
    
    // Only initialize once when modal opens
    if (open && position && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      
      setTpPrice(storeTakeProfit ? storeTakeProfit.toFixed(2) : "");
      setSlPrice(storeStopLoss ? storeStopLoss.toFixed(2) : "");
      setTpRoi("");
      setSlRoi("");
      setError(null);
      activeInputRef.current = null;
      setPartialTargets([]);
      
      // Calculate initial ROI values from existing TP/SL
      if (storeTakeProfit) {
        const isLong = position.side === "long";
        const priceDiff = isLong
          ? storeTakeProfit - position.entryPrice
          : position.entryPrice - storeTakeProfit;
        const percent = (priceDiff / position.entryPrice) * 100;
        if (percent > 0) setTpRoi(percent.toFixed(2));
      }
      if (storeStopLoss) {
        const isLong = position.side === "long";
        const priceDiff = isLong
          ? position.entryPrice - storeStopLoss
          : storeStopLoss - position.entryPrice;
        const percent = (priceDiff / position.entryPrice) * 100;
        if (percent > 0) setSlRoi(percent.toFixed(2));
      }
      
      // Load existing partial targets
      // This handles the case where user has already set partial targets
      setIsLoadingPartialTargets(true);
      PositionsApi.getTpSlTargets(position.market)
        .then((targets: TpSlTargetDto[]) => {
          const activeTargets = targets.filter(t => t.status === 'active');
          if (activeTargets.length > 0) {
            // Switch to partial tab if there are existing partial targets
            // and no standard TP/SL is set
            if (!storeTakeProfit && !storeStopLoss) {
              setTabMode("partial");
            }
            // Convert API format to UI format
            setPartialTargets(activeTargets.map(t => ({
              id: t.id,
              targetType: t.target_type,
              triggerPrice: parseFloat(t.trigger_price).toFixed(2),
              amountType: t.close_amount_type,
              amountValue: t.close_amount_value,
              roi: "", // Will be calculated when needed
            })));
          }
        })
        .catch((err) => {
          console.error("Failed to load partial targets:", err);
        })
        .finally(() => {
          setIsLoadingPartialTargets(false);
        });
    }
  }, [open, position, storeTakeProfit, storeStopLoss]);

  // Calculate TP price from ROI
  const calculateTpFromRoi = useCallback(
    (roiStr: string) => {
      if (!position || !roiStr) {
        setTpPrice("");
        return;
      }
      const roi = parseFloat(roiStr);
      if (isNaN(roi) || roi <= 0) return;

      const isLong = position.side === "long";
      const priceDiff = (roi / 100) * position.entryPrice;
      const newTpPrice = isLong
        ? position.entryPrice + priceDiff
        : position.entryPrice - priceDiff;

      if (newTpPrice > 0) {
        setTpPrice(newTpPrice.toFixed(2));
      }
    },
    [position]
  );

  // Calculate SL price from ROI
  const calculateSlFromRoi = useCallback(
    (roiStr: string) => {
      if (!position || !roiStr) {
        setSlPrice("");
        return;
      }
      const roi = parseFloat(roiStr);
      if (isNaN(roi) || roi <= 0) return;

      const isLong = position.side === "long";
      const priceDiff = (roi / 100) * position.entryPrice;
      const newSlPrice = isLong
        ? position.entryPrice - priceDiff
        : position.entryPrice + priceDiff;

      if (newSlPrice > 0) {
        setSlPrice(newSlPrice.toFixed(2));
      }
    },
    [position]
  );

  // Update ROI when TP price changes
  const updateTpRoiFromPrice = useCallback(
    (price: string) => {
      if (activeInputRef.current === "tpRoi") return;
      
      if (!position || !price) {
        setTpRoi("");
        return;
      }
      const tp = parseFloat(price);
      if (isNaN(tp) || tp <= 0) {
        setTpRoi("");
        return;
      }

      const isLong = position.side === "long";
      const priceDiff = isLong
        ? tp - position.entryPrice
        : position.entryPrice - tp;
      const percent = (priceDiff / position.entryPrice) * 100;
      
      if (percent > 0) {
        setTpRoi(percent.toFixed(2));
      } else {
        setTpRoi("");
      }
    },
    [position]
  );

  // Update ROI when SL price changes
  const updateSlRoiFromPrice = useCallback(
    (price: string) => {
      if (activeInputRef.current === "slRoi") return;
      
      if (!position || !price) {
        setSlRoi("");
        return;
      }
      const sl = parseFloat(price);
      if (isNaN(sl) || sl <= 0) {
        setSlRoi("");
        return;
      }

      const isLong = position.side === "long";
      const priceDiff = isLong
        ? position.entryPrice - sl
        : sl - position.entryPrice;
      const percent = (priceDiff / position.entryPrice) * 100;
      
      if (percent > 0) {
        setSlRoi(percent.toFixed(2));
      } else {
        setSlRoi("");
      }
    },
    [position]
  );

  // Handle TP price change
  const handleTpPriceChange = (value: string) => {
    setTpPrice(value);
    if (activeInputRef.current === "tpPrice") {
      updateTpRoiFromPrice(value);
    }
  };

  // Handle SL price change
  const handleSlPriceChange = (value: string) => {
    setSlPrice(value);
    if (activeInputRef.current === "slPrice") {
      updateSlRoiFromPrice(value);
    }
  };

  // Handle TP ROI change
  const handleTpRoiChange = (value: string) => {
    setTpRoi(value);
    calculateTpFromRoi(value);
  };

  // Handle SL ROI change
  const handleSlRoiChange = (value: string) => {
    setSlRoi(value);
    calculateSlFromRoi(value);
  };

  // Handle slider changes
  const handleTpSliderChange = (value: number) => {
    setTpRoi(value.toFixed(2));
    calculateTpFromRoi(value.toFixed(2));
  };

  const handleSlSliderChange = (value: number) => {
    setSlRoi(value.toFixed(2));
    calculateSlFromRoi(value.toFixed(2));
  };

  // Handle +/- buttons for price
  const incrementTpPrice = () => {
    const current = parseFloat(tpPrice) || (position?.entryPrice || 0);
    const step = current > 1000 ? 10 : current > 100 ? 1 : 0.1;
    const newValue = current + step;
    setTpPrice(newValue.toFixed(2));
    updateTpRoiFromPrice(newValue.toFixed(2));
  };

  const decrementTpPrice = () => {
    const current = parseFloat(tpPrice) || (position?.entryPrice || 0);
    const step = current > 1000 ? 10 : current > 100 ? 1 : 0.1;
    const newValue = Math.max(0, current - step);
    setTpPrice(newValue.toFixed(2));
    updateTpRoiFromPrice(newValue.toFixed(2));
  };

  const incrementSlPrice = () => {
    const current = parseFloat(slPrice) || (position?.entryPrice || 0);
    const step = current > 1000 ? 10 : current > 100 ? 1 : 0.1;
    const newValue = current + step;
    setSlPrice(newValue.toFixed(2));
    updateSlRoiFromPrice(newValue.toFixed(2));
  };

  const decrementSlPrice = () => {
    const current = parseFloat(slPrice) || (position?.entryPrice || 0);
    const step = current > 1000 ? 10 : current > 100 ? 1 : 0.1;
    const newValue = Math.max(0, current - step);
    setSlPrice(newValue.toFixed(2));
    updateSlRoiFromPrice(newValue.toFixed(2));
  };

  // Calculate expected profit/loss
  const tpResult = useCallback(() => {
    if (!position || !tpPrice) return null;
    const tp = parseFloat(tpPrice);
    if (tp <= 0) return null;

    const isLong = position.side === "long";
    const priceDiff = isLong
      ? tp - position.entryPrice
      : position.entryPrice - tp;
    const profitUsd = priceDiff * position.size;
    const profitPercent = (priceDiff / position.entryPrice) * 100;

    return { usd: profitUsd, percent: profitPercent, price: tp };
  }, [position, tpPrice])();

  const slResult = useCallback(() => {
    if (!position || !slPrice) return null;
    const sl = parseFloat(slPrice);
    if (sl <= 0) return null;

    const isLong = position.side === "long";
    const priceDiff = isLong
      ? position.entryPrice - sl
      : sl - position.entryPrice;
    const lossUsd = priceDiff * position.size;
    const lossPercent = (priceDiff / position.entryPrice) * 100;

    return { usd: lossUsd, percent: lossPercent, price: sl };
  }, [position, slPrice])();

  // Validate inputs
  const validateInputs = useCallback(() => {
    if (!position) return null;

    const isLong = position.side === "long";
    const tp = tpPrice ? parseFloat(tpPrice) : null;
    const sl = slPrice ? parseFloat(slPrice) : null;

    if (tp !== null && tp <= 0) {
      return "Take profit price must be positive";
    }

    if (sl !== null && sl <= 0) {
      return "Stop loss price must be positive";
    }

    if (isLong) {
      if (tp !== null && tp <= position.entryPrice) {
        return "TP must be above entry price for long";
      }
      if (sl !== null && sl >= position.entryPrice) {
        return "SL must be below entry price for long";
      }
    } else {
      if (tp !== null && tp >= position.entryPrice) {
        return "TP must be below entry price for short";
      }
      if (sl !== null && sl <= position.entryPrice) {
        return "SL must be above entry price for short";
      }
    }

    return null;
  }, [position, tpPrice, slPrice]);

  const handleSubmit = async () => {
    if (!effectiveUserId || !position) {
      toast.error("Please connect your wallet first");
      return;
    }

    // Route to appropriate handler based on mode
    if (tabMode === "partial") {
      await handlePartialSubmit();
    } else {
      await handleStandardSubmit();
    }
  };

  const handleStandardSubmit = async () => {
    const validationError = validateInputs();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const tp = tpPrice ? parseFloat(tpPrice) : null;
      const sl = slPrice ? parseFloat(slPrice) : null;

      const response = await PositionsApi.updateTpSl(position!.market, tp, sl);

      // Apply the updated position to the store for immediate UI update
      useAccountStore.getState().applyMutationResult({ position: response.position });

      toast.success("TP/SL updated successfully");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update TP/SL";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePartialSubmit = async () => {
    if (!effectiveUserId || !position) return;

    // Validation
    if (partialTargets.length === 0) {
      setError("Please add at least one target");
      return;
    }

    const isLong = position.side === "long";
    const positionSize = Math.abs(position.size);
    let totalPercentage = 0;
    let totalAbsolute = 0;
    const seenPrices = new Set<string>();

    // Comprehensive validation
    for (const target of partialTargets) {
      // Check trigger price is provided and positive
      const triggerPrice = parseFloat(target.triggerPrice);
      if (!target.triggerPrice || triggerPrice <= 0) {
        setError("All targets must have a valid trigger price");
        return;
      }

      // Check for duplicate trigger prices
      if (seenPrices.has(target.triggerPrice)) {
        setError(`Duplicate trigger price: $${target.triggerPrice}`);
        return;
      }
      seenPrices.add(target.triggerPrice);

      // Validate trigger price direction
      if (target.targetType === "take_profit") {
        if (isLong && triggerPrice <= position.entryPrice) {
          setError("Take profit for long must be above entry price");
          return;
        }
        if (!isLong && triggerPrice >= position.entryPrice) {
          setError("Take profit for short must be below entry price");
          return;
        }
      } else {
        // stop_loss
        if (isLong && triggerPrice >= position.entryPrice) {
          setError("Stop loss for long must be below entry price");
          return;
        }
        if (!isLong && triggerPrice <= position.entryPrice) {
          setError("Stop loss for short must be above entry price");
          return;
        }
      }

      // Check amount is provided and positive
      const amountValue = parseFloat(target.amountValue);
      if (!target.amountValue || amountValue <= 0) {
        setError("All targets must have a valid close amount");
        return;
      }

      // Track totals
      if (target.amountType === "percentage") {
        if (amountValue > 100) {
          setError("Percentage cannot exceed 100%");
          return;
        }
        totalPercentage += amountValue;
      } else {
        totalAbsolute += amountValue;
      }
    }

    // Check totals don't exceed position size
    if (totalPercentage > 100) {
      setError(`Total percentage targets (${totalPercentage.toFixed(1)}%) exceed 100%`);
      return;
    }

    if (totalAbsolute > positionSize) {
      setError(
        `Total absolute targets (${totalAbsolute.toFixed(4)}) exceed position size (${positionSize.toFixed(4)})`
      );
      return;
    }

    // Check combined percentage and absolute don't exceed position size
    const percentageAsSize = (totalPercentage / 100) * positionSize;
    if (percentageAsSize + totalAbsolute > positionSize) {
      setError("Combined targets exceed position size");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Convert to API format
      const targets: TpSlTarget[] = partialTargets.map((t) => ({
        target_type: t.targetType,
        trigger_price: t.triggerPrice,
        close_amount_type: t.amountType,
        close_amount_value: t.amountValue,
      }));

      const response = await PositionsApi.setTpSlTargets(position.market, targets);

      // Apply the updated position to the store
      useAccountStore.getState().applyMutationResult({ position: response.position });

      toast.success(`Successfully set ${targets.length} TP/SL target${targets.length > 1 ? 's' : ''}`);
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to set TP/SL targets";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!position) return null;

  const isLong = position.side === "long";
  const tpRoiNum = parseFloat(tpRoi) || 0;
  const slRoiNum = parseFloat(slRoi) || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-[520px] gap-0 p-0 overflow-hidden bg-card border-border"
        showCloseButton={false}
      >
        {/* Header */}
        <div className="px-7 pt-7 pb-4">
          <DialogHeader className="gap-0.5">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg font-semibold">
                Edit TP/SL
              </DialogTitle>
              <button
                onClick={() => onOpenChange(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground">
              {position.market}{" "}
              <span
                className={cn(
                  "font-medium",
                  isLong ? "text-bid-foreground" : "text-ask-foreground"
                )}
              >
                {isLong ? "Long" : "Short"}
              </span>{" "}
              position
            </p>
          </DialogHeader>
        </div>

        {/* Position Stats */}
        <div className="px-7 pb-4">
          <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Entry Price</span>
              <span className="text-foreground tabular-nums font-medium">
                ${formatCurrency(position.entryPrice, quotePrecision)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Price</span>
              <span className="text-foreground tabular-nums font-medium">
                ${formatCurrency(currentPrice, quotePrecision)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Size</span>
              <span className="text-foreground tabular-nums font-medium">
                {formatCurrency(position.size, 4)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current P&L</span>
              <span
                className={cn(
                  "tabular-nums font-medium",
                  currentPnl >= 0 ? "text-bid-foreground" : "text-ask-foreground"
                )}
              >
                ${formatCurrency(Math.abs(currentPnl), 2)}{" "}
                <span className="opacity-80">
                  ({currentPnlPercent >= 0 ? "+" : ""}
                  {currentPnlPercent.toFixed(2)}%)
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-border" />

        {/* Tabs */}
        <div className="px-7 pt-6">
          <div className="flex border-b border-border">
            <button
              onClick={() => {
                setTabMode("standard");
                setError(null);
              }}
              className={cn(
                "flex-1 pb-3 text-sm font-medium transition-colors relative",
                tabMode === "standard" 
                  ? "text-foreground" 
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Standard
              {tabMode === "standard" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-buy" />
              )}
            </button>
            <button
              onClick={() => {
                setTabMode("partial");
                setError(null);
              }}
              className={cn(
                "flex-1 pb-3 text-sm font-medium transition-colors relative",
                tabMode === "partial" 
                  ? "text-foreground" 
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Partial
              {tabMode === "partial" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-buy" />
              )}
            </button>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="overflow-y-auto max-h-[60vh] min-h-[340px]">
          {/* TP/SL Inputs */}
          {tabMode === "standard" && (
          <div className="px-7 py-6 space-y-6">
          {/* Take Profit Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">
                Take Profit - By ROI (%)
              </label>
              {tpResult && tpResult.usd > 0 && (
                <span className="text-xs text-muted-foreground">
                  Expected profit:{" "}
                  <span className="font-medium text-bid-foreground">
                    +${formatCurrency(Math.abs(tpResult.usd), 2)}
                  </span>
                </span>
              )}
            </div>
            
            {/* Price and ROI inputs */}
            <div className="grid grid-cols-[2fr_1fr] gap-3">
              <PriceInput
                value={tpPrice}
                onChange={handleTpPriceChange}
                onFocus={() => { activeInputRef.current = "tpPrice"; }}
                onBlur={() => { activeInputRef.current = null; }}
                onClear={() => { setTpPrice(""); setTpRoi(""); }}
                onIncrement={incrementTpPrice}
                onDecrement={decrementTpPrice}
                placeholder="TP price"
                suffix="USD"
              />
              <RoiInput
                value={tpRoi}
                onChange={handleTpRoiChange}
                onFocus={() => { activeInputRef.current = "tpRoi"; }}
                onBlur={() => { activeInputRef.current = null; }}
                onClear={() => { setTpRoi(""); setTpPrice(""); }}
                placeholder="ROI"
              />
            </div>
            
            {/* TP Slider */}
            <RoiSlider
              value={tpRoiNum}
              onChange={handleTpSliderChange}
              max={TP_SLIDER_MAX}
              color="green"
            />
          </div>

          {/* Stop Loss Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">
                Stop Loss - By ROI (%)
              </label>
              {slResult && slResult.usd > 0 && (
                <span className="text-xs text-muted-foreground">
                  Expected loss:{" "}
                  <span className="font-medium text-ask-foreground">
                    -${formatCurrency(Math.abs(slResult.usd), 2)}
                  </span>
                </span>
              )}
            </div>
            
            {/* Price and ROI inputs */}
            <div className="grid grid-cols-[2fr_1fr] gap-3">
              <PriceInput
                value={slPrice}
                onChange={handleSlPriceChange}
                onFocus={() => { activeInputRef.current = "slPrice"; }}
                onBlur={() => { activeInputRef.current = null; }}
                onClear={() => { setSlPrice(""); setSlRoi(""); }}
                onIncrement={incrementSlPrice}
                onDecrement={decrementSlPrice}
                placeholder="SL price"
                suffix="USD"
              />
              <RoiInput
                value={slRoi}
                onChange={handleSlRoiChange}
                onFocus={() => { activeInputRef.current = "slRoi"; }}
                onBlur={() => { activeInputRef.current = null; }}
                onClear={() => { setSlRoi(""); setSlPrice(""); }}
                placeholder="ROI"
              />
            </div>
            
            {/* SL Slider */}
            <RoiSlider
              value={slRoiNum}
              onChange={handleSlSliderChange}
              max={SL_SLIDER_MAX}
              color="red"
            />
          </div>

          {error && (
            <div className="text-xs text-destructive bg-destructive/10 p-3 rounded-lg text-center">
              {error}
            </div>
          )}
          </div>
          )}

          {/* Partial TP/SL View */}
          {tabMode === "partial" && (
          <div className="px-7 py-6 space-y-5">
          <p className="text-xs text-muted-foreground">
            Set multiple take profit and stop loss levels to close portions of your position at different prices.
          </p>
          
          {/* Loading state */}
          {isLoadingPartialTargets && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading existing targets...</span>
            </div>
          )}
          
          {/* Target List */}
          {!isLoadingPartialTargets && partialTargets.length > 0 && (
            <div className="space-y-3">
              {partialTargets.map((target, index) => (
                <div key={target.id} className="space-y-2 p-3 rounded-lg bg-muted/20 border border-border">
                  {/* Target Header */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">
                      Level {index + 1}: {target.targetType === "take_profit" ? "Take Profit" : "Stop Loss"}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setPartialTargets(partialTargets.filter((t) => t.id !== target.id));
                        setError(null);
                      }}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                  
                  {/* Trigger Price */}
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Trigger Price</label>
                    <PriceInput
                      value={target.triggerPrice}
                      onChange={(value) => {
                        setPartialTargets(
                          partialTargets.map((t) =>
                            t.id === target.id ? { ...t, triggerPrice: value } : t
                          )
                        );
                        setError(null);
                      }}
                      onClear={() => {
                        setPartialTargets(
                          partialTargets.map((t) =>
                            t.id === target.id ? { ...t, triggerPrice: "" } : t
                          )
                        );
                      }}
                      onIncrement={() => {
                        const current = parseFloat(target.triggerPrice) || 0;
                        const step = current > 1000 ? 10 : current > 100 ? 1 : 0.1;
                        setPartialTargets(
                          partialTargets.map((t) =>
                            t.id === target.id ? { ...t, triggerPrice: (current + step).toFixed(2) } : t
                          )
                        );
                      }}
                      onDecrement={() => {
                        const current = parseFloat(target.triggerPrice) || 0;
                        const step = current > 1000 ? 10 : current > 100 ? 1 : 0.1;
                        setPartialTargets(
                          partialTargets.map((t) =>
                            t.id === target.id ? { ...t, triggerPrice: Math.max(0, current - step).toFixed(2) } : t
                          )
                        );
                      }}
                      placeholder="Price"
                      suffix="USD"
                    />
                  </div>
                  
                  {/* Amount with Type Toggle */}
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Close Amount</label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 flex items-center rounded-lg border bg-muted/30 transition-all duration-200 overflow-hidden border-border hover:border-muted-foreground/60">
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder={target.amountType === "percentage" ? "50" : "2.5"}
                          value={target.amountValue}
                          onChange={(e) => {
                            const cleaned = e.target.value.replace(/[^0-9.]/g, "");
                            if (cleaned.length <= 12) {
                              setPartialTargets(
                                partialTargets.map((t) =>
                                  t.id === target.id ? { ...t, amountValue: cleaned } : t
                                )
                              );
                              setError(null);
                            }
                          }}
                          className={cn(
                            "flex-1 min-w-0 bg-transparent px-3 py-2.5 text-sm outline-none placeholder:text-muted-foreground/50",
                            "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          )}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setPartialTargets(
                              partialTargets.map((t) =>
                                t.id === target.id
                                  ? {
                                      ...t,
                                      amountType: t.amountType === "percentage" ? "absolute" : "percentage",
                                      amountValue: "", // Clear value when switching
                                    }
                                  : t
                              )
                            );
                          }}
                          className={cn(
                            "flex items-center gap-1 pr-3 pl-2 py-2.5 text-xs transition-colors",
                            "text-muted-foreground/70 hover:text-foreground"
                          )}
                          title={`Switch to ${target.amountType === "percentage" ? "absolute" : "percentage"}`}
                        >
                          <ArrowLeftRight className="size-2.5" />
                          {target.amountType === "percentage" ? "%" : position?.market.split("/")[0]}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Add Target Buttons */}
          {!isLoadingPartialTargets && (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => {
                setPartialTargets([
                  ...partialTargets,
                  {
                    id: crypto.randomUUID(),
                    targetType: "take_profit",
                    triggerPrice: "",
                    amountType: "percentage",
                    amountValue: "",
                    roi: "",
                  },
                ]);
              }}
            >
              <Plus className="size-3.5 mr-1" />
              Add Take Profit
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => {
                setPartialTargets([
                  ...partialTargets,
                  {
                    id: crypto.randomUUID(),
                    targetType: "stop_loss",
                    triggerPrice: "",
                    amountType: "percentage",
                    amountValue: "",
                    roi: "",
                  },
                ]);
              }}
            >
              <Plus className="size-3.5 mr-1" />
              Add Stop Loss
            </Button>
          </div>
          )}
          
          {/* Summary */}
          {!isLoadingPartialTargets && partialTargets.length > 0 && (
            <div className="p-3 rounded-lg bg-muted/10 border border-border text-xs space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Targets:</span>
                <span className="font-medium">{partialTargets.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Take Profit Levels:</span>
                <span className="font-medium text-bid-foreground">
                  {partialTargets.filter((t) => t.targetType === "take_profit").length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Stop Loss Levels:</span>
                <span className="font-medium text-ask-foreground">
                  {partialTargets.filter((t) => t.targetType === "stop_loss").length}
                </span>
              </div>
            </div>
          )}
          
          {error && (
            <div className="text-xs text-destructive bg-destructive/10 p-3 rounded-lg text-center">
              {error}
            </div>
          )}
          </div>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="flex gap-3 px-7 pb-7 pt-4 border-t border-border">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 h-11"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1 h-11 bg-buy hover:bg-buy/90 text-white"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Confirm"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
